import { forwardRef, useState, useEffect } from 'react';
import { Quotation, CompanySettings, DEFAULT_COMPANY_SETTINGS, getCompanySettings } from '@/services/firestore';

interface QuotationDocumentProps {
    data: Quotation;
    company?: CompanySettings;
}

export const QuotationDocument = forwardRef<HTMLDivElement, QuotationDocumentProps>(({ data, company: propCompany }, ref) => {
    const [company, setCompany] = useState<CompanySettings>(propCompany || DEFAULT_COMPANY_SETTINGS);

    useEffect(() => {
        if (propCompany) {
            setCompany(propCompany);
        } else {
            getCompanySettings().then(setCompany).catch(() => {});
        }
    }, [propCompany]);

    const f = (n: number | string | undefined) => {
        if (n === undefined || n === null || n === '') return '—';
        if (typeof n === 'string' && isNaN(Number(n))) return n;
        const num = typeof n === 'string' ? parseFloat(n) : n;
        return num.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const subtotal = data.subtotal || data.items.reduce((acc, it) => {
        const val = typeof it.total === 'number' ? it.total : parseFloat(String(it.total));
        return acc + (isNaN(val) ? 0 : val);
    }, 0);

    const discountAmount = data.discountAmount || 0;
    const taxAmount = data.taxAmount || 0;
    const totalAmount = data.totalAmount || (subtotal - discountAmount + taxAmount);

    return (
        <div
            ref={ref}
            className="bg-white p-10 w-[210mm] min-h-[297mm] mx-auto text-black font-sans relative flex flex-col justify-between"
            style={{ boxSizing: 'border-box' }}
        >
            <div>
                {/* Header / Logo Section */}
                <div className="flex items-center justify-between border-b-[3px] border-black pb-3 mb-6 relative">
                    <div className="flex items-center gap-4">
                        <img
                            src="/logo.png"
                            alt="Logo"
                            className="h-16 object-contain"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.parentElement) {
                                    e.currentTarget.parentElement.innerHTML = '<div class="h-14 w-28 border-2 border-black flex items-center justify-center font-black text-xl italic tracking-wider">POS</div>';
                                }
                            }}
                        />
                        <div>
                            <h1 className="text-2xl font-black italic tracking-wider text-black m-0 leading-none">
                                {company.companyName}
                            </h1>
                            {company.tagline && (
                                <p className="font-semibold text-[11px] tracking-[0.2em] uppercase mt-1 text-slate-700">
                                    {company.tagline}
                                </p>
                            )}
                            <p className="text-[10px] text-slate-600 font-medium mt-0.5">
                                {company.address}{company.city ? `, ${company.city}` : ''}
                                {company.phone ? ` | Tel: ${company.phone}` : ''}
                                {company.email ? ` | ${company.email}` : ''}
                                {company.ntn ? ` | NTN: ${company.ntn}` : ''}
                                {company.stn ? ` | STN: ${company.stn}` : ''}
                            </p>
                        </div>
                    </div>

                    <div className="text-right">
                        <div className="inline-block border-2 border-black px-4 py-1 font-black text-sm uppercase tracking-wider bg-slate-100">
                            QUOTATION
                        </div>
                        <div className="mt-1.5 font-mono text-xs font-bold text-slate-800">
                            #{data.quotationNumber}
                        </div>
                        <div className="text-[11px] text-slate-600 font-medium">
                            Date: <strong>{data.date}</strong>
                        </div>
                        {data.validUntil && (
                            <div className="text-[10px] text-slate-500 font-medium">
                                Valid Until: <strong className="text-black">{data.validUntil}</strong>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recipient / Client Details */}
                <div className="grid grid-cols-2 gap-6 mb-6 text-[12px] bg-slate-50 p-4 border border-slate-300 rounded-lg">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                            QUOTED TO:
                        </div>
                        <div className="font-black text-sm text-black">{data.companyName}</div>
                        {data.companyAddress && <div className="text-slate-700 mt-0.5">{data.companyAddress}</div>}
                        {data.customerPhone && <div className="text-slate-600 mt-0.5">Phone: {data.customerPhone}</div>}
                        {data.customerEmail && <div className="text-slate-600">Email: {data.customerEmail}</div>}
                        {data.customerNtn && <div className="text-slate-600">NTN: <strong className="text-black">{data.customerNtn}</strong></div>}
                    </div>

                    <div className="border-l border-slate-300 pl-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                            ATTENTION:
                        </div>
                        {data.attnName1 && (
                            <div className="font-bold text-slate-800">
                                {data.attnName1} {data.attnRole1 ? `(${data.attnRole1})` : ''}
                            </div>
                        )}
                        {data.attnName2 && (
                            <div className="text-slate-700 mt-0.5">
                                {data.attnName2} {data.attnRole2 ? `(${data.attnRole2})` : ''}
                            </div>
                        )}
                        <div className="mt-2 text-[11px] text-slate-600">
                            Status: <strong className="uppercase text-blue-700">{data.status || 'Draft'}</strong>
                        </div>
                    </div>
                </div>

                {/* Subject Line */}
                <div className="mb-4 bg-slate-100/70 p-2.5 border-l-4 border-black text-[13px]">
                    <span className="font-bold uppercase tracking-wider text-[11px] text-slate-500 mr-2">
                        Subject:
                    </span>
                    <span className="font-bold text-black underline underline-offset-2">
                        {data.subject}
                    </span>
                </div>

                <div className="mb-3 text-[12px] font-semibold text-slate-700">
                    We are pleased to quote our best prices for the following items/specifications:
                </div>

                {/* Items Table */}
                <table className="w-full border-collapse border-2 border-black text-[11px] mb-6">
                    <thead>
                        <tr className="bg-slate-200 border-b-2 border-black text-black">
                            <th className="border border-black px-2 py-1.5 w-10 text-center font-bold">S.#</th>
                            <th className="border border-black px-3 py-1.5 text-left font-bold">Item Description &amp; Specifications</th>
                            <th className="border border-black px-2 py-1.5 w-20 text-center font-bold">Brand / Make</th>
                            <th className="border border-black px-2 py-1.5 w-20 text-center font-bold">Item Code</th>
                            <th className="border border-black px-2 py-1.5 w-14 text-center font-bold">UOM</th>
                            <th className="border border-black px-2 py-1.5 w-14 text-center font-bold">Qty</th>
                            <th className="border border-black px-2 py-1.5 w-24 text-right font-bold">Unit Rate</th>
                            <th className="border border-black px-2 py-1.5 w-28 text-right font-bold">Total (PKR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((item, index) => (
                            <tr key={index} className="border-b border-black">
                                <td className="border border-black px-2 py-2 text-center font-bold">{item.sNo || index + 1}</td>
                                <td className="border border-black px-3 py-2 text-left font-medium leading-snug break-words">
                                    {item.description}
                                    {item.hsCode && <span className="block text-[9px] text-slate-500">HS Code: {item.hsCode}</span>}
                                </td>
                                <td className="border border-black px-2 py-2 text-center text-slate-700">{item.brand || '—'}</td>
                                <td className="border border-black px-2 py-2 text-center font-mono text-slate-700">{item.itemCode || '—'}</td>
                                <td className="border border-black px-2 py-2 text-center">{item.uom || 'PCS'}</td>
                                <td className="border border-black px-2 py-2 text-center font-bold">{item.quantity}</td>
                                <td className="border border-black px-2 py-2 text-right font-mono font-medium">
                                    {typeof item.unitRate === 'number' ? f(item.unitRate) : item.unitRate}
                                </td>
                                <td className="border border-black px-2 py-2 text-right font-mono font-bold">
                                    {typeof item.total === 'number' ? f(item.total) : item.total}
                                </td>
                            </tr>
                        ))}
                        {data.items.length === 0 && (
                            <tr>
                                <td colSpan={8} className="border border-black p-6 text-center text-slate-400 italic">
                                    No items listed in this quotation
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Totals Summary */}
                <div className="flex justify-end mb-6">
                    <div className="w-72 border-2 border-black bg-slate-50 text-[12px]">
                        <div className="flex justify-between px-3 py-1.5 border-b border-slate-300">
                            <span className="text-slate-600 font-medium">Subtotal:</span>
                            <span className="font-mono font-bold">{f(subtotal)}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className="flex justify-between px-3 py-1.5 border-b border-slate-300 text-red-600">
                                <span>Discount:</span>
                                <span className="font-mono font-bold">- {f(discountAmount)}</span>
                            </div>
                        )}
                        {taxAmount > 0 && (
                            <div className="flex justify-between px-3 py-1.5 border-b border-slate-300">
                                <span className="text-slate-600 font-medium">Sales Tax ({data.taxRate || 18}%):</span>
                                <span className="font-mono font-bold">+ {f(taxAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between px-3 py-2 bg-slate-200 font-black text-[13px] border-t-2 border-black">
                            <span>Total Amount (PKR):</span>
                            <span className="font-mono">{f(totalAmount)}</span>
                        </div>
                    </div>
                </div>

                {/* Terms & Conditions & Notes */}
                {(data.termsAndConditions || data.notes) && (
                    <div className="mb-6 p-4 border border-slate-300 bg-slate-50 rounded text-[11px] space-y-2">
                        {data.termsAndConditions && (
                            <div>
                                <span className="font-bold text-black uppercase tracking-wider block mb-1">
                                    Terms &amp; Conditions:
                                </span>
                                <div className="text-slate-700 whitespace-pre-line leading-relaxed">
                                    {data.termsAndConditions}
                                </div>
                            </div>
                        )}
                        {data.notes && (
                            <div className="pt-2 border-t border-slate-200">
                                <span className="font-bold text-black uppercase tracking-wider block mb-1">
                                    Notes:
                                </span>
                                <div className="text-slate-700 whitespace-pre-line leading-relaxed">
                                    {data.notes}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer Signatures */}
            <div className="pt-4 border-t-2 border-black/40 text-[12px]">
                <div className="flex justify-between items-end">
                    <div>
                        <div className="font-bold text-black">Taj Muhammad Salik ( BDM )</div>
                        <div className="font-semibold italic text-slate-700">Dynamic Industrial Solutions</div>
                        <div className="text-[10px] text-slate-500 mt-1">Authorized Commercial Signature</div>
                        <div className="mt-8 border-b-2 border-black w-48"></div>
                    </div>

                    <div className="text-right">
                        <div className="font-bold text-black">Customer Acceptance</div>
                        <div className="text-[10px] text-slate-500 mt-1">Sign &amp; Stamp to Confirm Order</div>
                        <div className="mt-8 border-b-2 border-black w-48 ml-auto"></div>
                    </div>
                </div>
            </div>

            <style>
                {`
                    @media print {
                        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; background: transparent; }
                        @page { size: A4 portrait; margin: 8mm; }
                    }
                `}
            </style>
        </div>
    );
});

QuotationDocument.displayName = 'QuotationDocument';
